import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/enums/routes";
import { createOrg } from "@/modules/organizationServices";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { organizationSchema } from "@/utils/schemaHelper";
import { OrganizationValidate } from "@/enums/errorMessages";
import { updateDetails } from "@/store/redux/userDetailsSlice";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";
import { setLoading } from "@/store/redux/appSlice";
import { Card } from "@/uiComponents/components/ui/card";

import { FiCheckCircle } from "react-icons/fi";

export function OrganizationForm() {
  const user = useSelector((state: RootState) => state.auth.profile);
  const accessToken = user?.accessToken;
  const dispatch = useDispatch();
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const role = useSelector((state: RootState) => state.userDetail.role);
  const navigate = useNavigate();
  const loading = useSelector((state: RootState) => state.app.loading);

  const form = useForm<z.infer<typeof organizationSchema>>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      organizationName: "",
    },
  });

  useEffect(() => {
    if (userDetails?.organizationId != null) {
      navigate(ROUTES.DASHBOARD);
    }
  }, []);

  const onSubmit = async (values: z.infer<typeof organizationSchema>) => {
    if (loading) return;
    dispatch(setLoading(true));
    try {
      if (!accessToken) return;
      await createOrg(values, role);

      if (userDetails) {
        const updateItems = {
          id: userDetails?.id,
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          email: userDetails?.email,
          username: userDetails?.username,
          role: userDetails?.role,
          branch: userDetails.branch,
          branchName: userDetails.branchName,
          organizationId: userDetails.organizationId,
          ...values,
        };
        dispatch(updateDetails(updateItems));
      }
      navigate(ROUTES.DASHBOARD);
      toastMessage("success", OrganizationValidate.ORGANIZATION_CREATED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const features = [
    {
      title: "Role-Based Access",
      description:
        "Super Admins can create and manage all organizations and branches. Organization Admins can manage branches and categories within their organization. All users, including admins, can log expenses.",
    },
    {
      title: "Expense Management",
      description:
        "   Generate PDF reports, filter expenses, and visualize spending through branch/category pie charts and date-based line graphs",
    },
    {
      title: "Tagging System",
      description:
        "Categorize expenses with custom tags for better organization and filtering",
    },
    {
      title: "Expense Tracking",
      description: "Monitor and reduce your organization's expense over time.",
    },
  ];

  return (
    <div className="md:h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-5xl p-8 shadow-lg rounded-xl">
        <h1 className="text-3xl mb-3 font-bold text-gray-800 text-center">
          Welcome to Expense Tracking System
        </h1>
        <div className="flex flex-col md:flex-row gap-8">
          {/* Information Section */}
          <div className="md:w-1/2 space-y-6">
            <p className="text-lg text-gray-600">
              <b>Get started</b> by creating your organization. This will be
              your workspace where you can track expenses, manage teams, and
              analyze spending patterns.
            </p>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-700">
                Expense Tracking system allows you to:
              </h3>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-800">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h4 className="font-semibold text-blue-800 mb-2">
                <u>Note</u>
              </h4>
              <p className="text-blue-700 text-sm">
                Choose your Organization name carefully as it cannot be changed
                later. Make sure it accurately represents your organization.
              </p>
            </div>
          </div>

          {/* Form Section */}
          <div className="md:w-1/2 md:mt-10">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6 p-6 bg-white rounded-lg border border-gray-200"
              >
                <div>
                  <h2 className="text-2xl font-semibold text-gray-800 mb-1">
                    Create Your Organization
                  </h2>
                </div>

                <FormField
                  control={form.control}
                  name="organizationName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700">
                        Organization Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="w-full"
                          placeholder="Demo Org."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-2">
                  <ReusableButton
                    label="Create Organization"
                    className="w-full"
                    loading={loading}
                  />
                </div>

                <p className="text-sm text-gray-500 text-center">
                  By creating an organization, you agree to our Terms of Service
                  and Privacy Policy.
                </p>
              </form>
            </Form>
          </div>
        </div>
      </Card>
    </div>
  );
}
